import React, { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import { supabase } from "../supabase";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Login from "./Login";
import { TbLivePhotoFilled } from "react-icons/tb";
import Loader from "./Loader";
import GoToTop from "../GoToTop";
import { FaStar } from "react-icons/fa";
import { IoClose } from "react-icons/io5";

function MyBookings() {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [rating, setRating] = useState(0);
  const [driverRating, setDriverRating] = useState(0);
  const [review, setReview] = useState("");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  // Auth user
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsub();
  }, []);

  const fetchAllBookingsData = async (firestoreBookings = []) => {
    // 🔹 Supabase Bookings
    const { data: supabaseBookings, error } = await supabase
      .from("Bookings")
      .select("*")
      .eq("customer_id", user.uid)
      .order("id", { ascending: false });

    let formattedSupabase = [];

    if (!error && supabaseBookings) {
      formattedSupabase = supabaseBookings.map((item) => ({
        id: item.id,
        source: "supabase",
        ...item,
      }));
    }

    // 🔹 Merge
    const mergedBookings = [...firestoreBookings, ...formattedSupabase];

    // 🔹 Fetch Ratings
    const { data: ratingsData } = await supabase
      .from("Ratings")
      .select("*")
      .eq("customer_id", user.uid);

    // 🔹 Attach ratings + images
    const bookingsWithExtras = await Promise.all(
      mergedBookings.map(async (booking) => {
        try {
          let vehicle_image = booking.vehicle_image;

          if (booking.source === "supabase") {
            const { data } = await supabase
              .from("Vehicles")
              .select("img_url")
              .eq("id", booking.vehicle_id)
              .single();

            vehicle_image = data?.img_url || "/default-car.png";
          }

          // ✅ Find rating for this booking
          const ratingData = ratingsData?.find(
            (r) => r.booking_id === booking.id,
          );

          return {
            ...booking,
            vehicle_image,
            is_reviewed: !!ratingData, // ✅ true/false
            ride_rating: ratingData?.ride_rating || null,
          };
        } catch (err) {
          console.error(err);
          return booking;
        }
      }),
    );

    return bookingsWithExtras;
  };
  // Fetch booking
  useEffect(() => {
    if (!user?.uid) return;

    let firestoreUnsub;

    const fetchAllBookings = async () => {
      setLoading(true);

      // 🔹 1️⃣ Firestore Fetch
      const q = query(
        collection(db, "LiveBookings"),
        where("customer_id", "==", user.uid),
      );

      firestoreUnsub = onSnapshot(q, async (snapshot) => {
        let firestoreBookings = [];

        if (!snapshot.empty) {
          firestoreBookings = snapshot.docs.map((doc) => ({
            id: doc.id,
            source: "firestore",
            ...doc.data(),
          }));
        }

        // 🔹 2️⃣ Supabase Fetch

        const bookingsWithImages =
          await fetchAllBookingsData(firestoreBookings);
        setBookings(bookingsWithImages);

        setLoading(false);
        setLoading(false);
      });
    };

    fetchAllBookings();

    return () => {
      if (firestoreUnsub) firestoreUnsub();
    };
  }, [user]);

  const handleGoogleLogin = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      toast.success("Logged in successfully!");
    } catch (error) {
      console.error("Google Login Error:", error);
      toast.error("Login failed. Try again.");
    }
  };

  const submitReview = async () => {
    if (!selectedBooking) return;

    try {
      // ✅ 1. CHECK FIRST (put it here)
      const { data: existing, error: checkError } = await supabase
        .from("Ratings")
        .select("*")
        .eq("booking_id", selectedBooking.id)
        .maybeSingle(); // 👈 safer than .single()

      if (checkError) throw checkError;

      if (existing) {
        toast.error("Already reviewed!");
        return; // 🚫 stop execution
      }

      // ✅ 2. INSERT REVIEW
      const { error } = await supabase.from("Ratings").insert([
        {
          booking_id: selectedBooking.id,
          vehicle_id: selectedBooking.vehicle_id,
          driver_id: selectedBooking.driver_id,
          customer_id: user.uid,
          ride_rating: rating,
          driver_rating: driverRating,
          review: review,
        },
      ]);

      if (error) throw error;

      toast.success("Review submitted!");

      // ✅ 3. OPTIONAL: mark booking as reviewed
      await supabase
        .from("Bookings")
        .update({ is_reviewed: true })
        .eq("id", selectedBooking.id);

      // ✅ 4. RESET UI
      setShowModal(false);

      // ✅ Refetch bookings (THIS WAS MISSING)
      const firestoreOnly = bookings.filter((b) => b.source === "firestore");
      const updatedBookings = await fetchAllBookingsData(firestoreOnly);
      setBookings(updatedBookings);

      setRating(0);
      setDriverRating(0);
      setReview("");
      setSelectedBooking(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit review");
    }
  };

  if (!user) {
    return <Login handleGoogleLogin={handleGoogleLogin} />;
  }

  return (
    <>
      <GoToTop />
      <div className="container web-main" style={{ marginTop: "90px" }}>
        <h6 style={{ color: "grey" }}>My Bookings</h6>

        {loading ? (
          <Loader />
        ) : bookings.length == 0 ? (
          <>
            <img
              style={{ height: "190px", width: "100%", objectFit: "contain" }}
              src="https://cdn.dribbble.com/userupload/23148934/file/original-92092ca2c6eff53b86b810e1a2f9b88a.gif"
              className="img-fluid mt-5"
            />
            <p className="text-center" style={{ color: "grey" }}>
              No Bookings Found
            </p>
          </>
        ) : (
          bookings.map((data) => (
            <>
              <div
                className="booking-ride-card mb-2" style={{cursor:'pointer'}}
                key={data.id}
                onClick={() => {
                  if (data.source === "firestore") {
                    navigate("/my-ride-details");
                  } else {
                    navigate(`/my-bookings-details/${data.id}`);
                  }
                }}
              >
                <div className="booking-ride-left">
                  <div className="dot pickup"></div>
                  <div className="line"></div>
                  <div className="dot drop"></div>
                </div>

                <div className="booking-ride-details">
                  <div className="booking-location">
                    <p className="label">Pickup</p>
                    <p className="value">{data.pickup}</p>
                  </div>

                  <div className="booking-location">
                    <p className="label">Drop</p>
                    <p className="value">{data.drop}</p>
                  </div>

                  <div className="booking-footer">
                    <span className="booking-type">{data.booking_type}</span>
                    {data.source === "supabase" && !data.is_reviewed ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();

                          setSelectedBooking(data);
                          setShowModal(true);
                        }}
                        className="review-btn"
                      >
                        Review
                      </button>
                    ) : data.source === "supabase" && data.is_reviewed ? (
                      <p style={{ margin: 0 }}>
                        ⭐ {data.ride_rating}
                        <span style={{ color: "grey", fontSize: "12.5px" }}>
                            {" "}/5
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          ))
        )}
      </div>

      {showModal && (
        <div className="bottom-modal-overlay">
          <div className="bottom-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="modal-header">
              <h5>Your Review</h5>
              <IoClose
                className="close-icon"
                onClick={() => {
                  setShowModal(false);
                  setRating(0);
                }}
              />
            </div>

            <label className="form-label mb-0 mt-3">Rate your ride</label>
            <div className="mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  size={25}
                  color={star <= rating ? "#ffc107" : "#e4e5e9"}
                  onClick={() => setRating(star)}
                  style={{ cursor: "pointer", marginRight: "4px" }}
                />
              ))}
            </div>

            <label className="form-label mb-0 mt-3">Rate the driver</label>
            <div className="mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  size={25}
                  color={star <= driverRating ? "#ffc107" : "#e4e5e9"}
                  onClick={() => setDriverRating(star)}
                  style={{ cursor: "pointer", marginRight: "4px" }}
                />
              ))}
            </div>

            <label className="form-label mb-0 mt-3">Overall Review</label>
            <textarea
              placeholder="Write your review..."
              className="form-control mb-2"
              value={review}
              rows={5}
              onChange={(e) => setReview(e.target.value)}
            />

            <button className="btn btn-dark w-100" onClick={submitReview}>
              Submit Review
            </button>

            {/* <button
        className="btn btn-secondary w-100 mt-2"
        onClick={() => setShowModal(false)}
      >
        Cancel
      </button> */}
          </div>
        </div>
      )}
    </>
  );
}

export default MyBookings;
